import React from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {
  IKpiObjectiveContractSavePayload,
  IKpiContractResponsibility,
  IKpiContractCompetence,
  IKpiContractIndicator,
  IKpiContractObjective,
  IKpiContractValidation,
} from './objectivecontract.models';
import {
  fetchObjectiveContract,
  saveObjectiveContract,
  fetchObjectiveContractGeneratedPayload,
} from './objectivecontract.apis';
import { emptyObjectiveContract } from './objectivecontract.defaults';
import IdentificationTab from './tabs/IdentificationTab';
import MissionTab from './tabs/MissionTab';
import ResponsibilitiesTab from './tabs/ResponsibilitiesTab';
import RelationsTab from './tabs/RelationsTab';
import ProfileTab from './tabs/ProfileTab';
import CompetencesTab from './tabs/CompetencesTab';
import IndicatorsTab from './tabs/IndicatorsTab';
import ObjectivesTab from './tabs/ObjectivesTab';
import ValidationTab from './tabs/ValidationTab';
import SectionCard from './components/SectionCard';
import { PageProps } from '../../../components/types/coretypes';

//type Props = { contractix?: string; onSaved?: (recix: string) => void };
export interface Props extends PageProps {
  contractix?: string; 
  onSaved?: (recix: string) => void
}

type State = {
  step: number;
  loading: boolean;
  saving: boolean;
  message: string;
  error: string;
  preview: any;
  data: IKpiObjectiveContractSavePayload;
};
const steps = [
  'Identification',
  'Mission',
  'Responsabilités',
  'Relations',
  'Profil',
  'Compétences',
  'Indicateurs',
  'Objectifs',
  'Validation / Preview',
];

export class ObjectiveContractBuilder extends React.Component<Props, State> {
  state: State = {
    step: 0,
    loading: false,
    saving: false,
    message: '',
    error: '',
    preview: null,
    data: emptyObjectiveContract(),
  };
  async componentDidMount() {
    await this.loadContract();
  }
  async componentDidUpdate(prevProps: Props) {
    if (prevProps.contractix !== this.props.contractix)
      await this.loadContract();
  }

  private loadContract = async () => {
    const { contractix } = this.props;
    if (!contractix) {
      this.setState({ data: emptyObjectiveContract(), preview: null });
      return;
    }
    try {
      this.setState({ loading: true, error: '', message: '' });
      const data = await fetchObjectiveContract(contractix);
      this.setState({ data });
    } catch (e: any) {
      this.setState({ error: e?.message || `${e}` });
    } finally {
      this.setState({ loading: false });
    }
  };

  private setContract = (
    patch: Partial<IKpiObjectiveContractSavePayload['contract']>
  ) => {
    this.setState((prev) => ({
      data: { ...prev.data, contract: { ...prev.data.contract, ...patch } },
    }));
  };
  private setResponsibilities = (
    responsibilities: IKpiContractResponsibility[]
  ) => this.setState((prev) => ({ data: { ...prev.data, responsibilities } }));
  private setCompetences = (competences: IKpiContractCompetence[]) =>
    this.setState((prev) => ({ data: { ...prev.data, competences } }));
  private setIndicators = (indicators: IKpiContractIndicator[]) =>
    this.setState((prev) => ({ data: { ...prev.data, indicators } }));
  private setObjectives = (objectives: IKpiContractObjective[]) =>
    this.setState((prev) => ({ data: { ...prev.data, objectives } }));
  private setValidations = (validations: IKpiContractValidation[]) =>
    this.setState((prev) => ({ data: { ...prev.data, validations } }));

  private handleSave = async () => {
    try {
      this.setState({ saving: true, error: '', message: '' });
      const saved = await saveObjectiveContract(this.state.data);
      const recix =
        saved?.recix ||
        saved?.contractix ||
        this.state.data.contract.recix ||
        '';
      this.setState((prev) => ({
        message: 'Contract saved successfully.',
        data: { ...prev.data, contract: { ...prev.data.contract, recix } },
      }));
      if (recix && this.props.onSaved) this.props.onSaved(recix);
    } catch (e: any) {
      this.setState({ error: e?.message || `${e}` });
    } finally {
      this.setState({ saving: false });
    }
  };

  private handlePreview = async () => {
    const recix = this.state.data.contract.recix;
    if (!recix) {
      this.setState({
        error: 'Save the contract before generating preview payload.',
      });
      return;
    }
    try {
      this.setState({ error: '' });
      const preview = await fetchObjectiveContractGeneratedPayload(recix);
      this.setState({ preview, step: 8 });
    } catch (e: any) {
      this.setState({ error: e?.message || `${e}` });
    }
  };

  private next = () =>
    this.setState((prev) => ({
      step: Math.min(prev.step + 1, steps.length - 1),
    }));
  private back = () =>
    this.setState((prev) => ({ step: Math.max(prev.step - 1, 0) }));

  private renderStep() {
    const { step, data, preview } = this.state;
    switch (step) {
      case 0:
        return <IdentificationTab data={data} setContract={this.setContract} />;
      case 1:
        return <MissionTab data={data} setContract={this.setContract} />;
      case 2:
        return (
          <ResponsibilitiesTab
            items={data.responsibilities}
            onChange={this.setResponsibilities}
          />
        );
      case 3:
        return <RelationsTab data={data} setContract={this.setContract} />;
      case 4:
        return <ProfileTab data={data} setContract={this.setContract} />;
      case 5:
        return (
          <CompetencesTab
            items={data.competences}
            onChange={this.setCompetences}
          />
        );
      case 6:
        return (
          <IndicatorsTab
            items={data.indicators}
            onChange={this.setIndicators}
          />
        );
      case 7:
        return (
          <ObjectivesTab
            items={data.objectives}
            onChange={this.setObjectives}
          />
        );
      case 8:
        return (
          <>
            <ValidationTab
              items={data.validations}
              onChange={this.setValidations}
            />
            <SectionCard
              title="Preview Payload"
              subtitle="Payload used for DOCX/PDF generation."
            >
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(preview || data, null, 2)}
              </pre>
            </SectionCard>
          </>
        );
      default:
        return null;
    }
  }

  render() {
    const { step, loading, saving, message, error } = this.state;
    if (loading) return <Typography>Loading contract...</Typography>;
    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            KPI Objective Contract Builder
          </Typography>
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={saving}
            onClick={this.handleSave}
          >
            Save
          </Button>
          <Button variant="outlined" onClick={this.handlePreview}>
            Preview Payload
          </Button>
        </Stack>
        {message ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stepper activeStep={step} alternativeLabel nonLinear>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepButton onClick={() => this.setState({ step: index })}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Paper>
        {this.renderStep()}
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button disabled={step === 0} onClick={this.back}>
            Back
          </Button>
          <Button
            variant="contained"
            disabled={step === steps.length - 1}
            onClick={this.next}
          >
            Next
          </Button>
        </Stack>
      </Box>
    );
  }
}
